// The web's mirror of NormalizePhone. See pricing.ts for why these exist twice.

export const PHONE_MIN_DIGITS = 8;
export const PHONE_MAX_DIGITS = 15;

export type PhoneErrorCode = 'PHONE_REQUIRED' | 'PHONE_INVALID';

export class PhoneError extends Error {
	constructor(readonly code: PhoneErrorCode) {
		super(code);
		this.name = 'PhoneError';
	}
}

/**
 * Turns what was typed into E.164.
 *
 *   a) spaces, dashes and parentheses go
 *   b) a leading "+" keeps the rest; a leading "00" becomes "+";
 *      otherwise the country code is prefixed and one leading "0" is dropped
 *   c) 8 to 15 digits, or PHONE_INVALID
 *   d) "+" and the digits
 *
 * The trunk prefix "0" and the international prefix "00" are assumed, which
 * holds for Egypt and most ITU countries and not for North America.
 */
export function normalizePhone(input: string, countryCode: string): string {
	const cleaned = input.trim().replace(/[\s\-()]/g, '');
	if (cleaned === '') throw new PhoneError('PHONE_REQUIRED');

	let digits: string;
	if (cleaned.startsWith('+')) {
		digits = cleaned.slice(1);
	} else if (cleaned.startsWith('00')) {
		digits = cleaned.slice(2);
	} else {
		// One leading zero is the trunk prefix and is not part of the number.
		// A second would be a typo, and is left to fail the length check
		// rather than be silently eaten.
		const national = cleaned.startsWith('0') ? cleaned.slice(1) : cleaned;
		digits = countryCode.trim().replace(/^\+/, '') + national;
	}

	if (
		!/^\d+$/.test(digits) ||
		digits.length < PHONE_MIN_DIGITS ||
		digits.length > PHONE_MAX_DIGITS
	) {
		throw new PhoneError('PHONE_INVALID');
	}
	return '+' + digits;
}
